import React, { useState } from "react";
import { Clock, MessageCircle, X, MapPin, Check, AlertTriangle } from "lucide-react";
import { EXCURSIONS, buildWhatsAppLink } from "../mock/mock";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const WaveDivider = () => (
  <svg viewBox="0 0 200 20" className="h-4 w-32 text-[#25D366]" fill="none">
    <path
      d="M0 10 Q 25 0, 50 10 T 100 10 T 150 10 T 200 10"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

const Excursions = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="excursions" className="py-16 lg:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-center gap-4 mb-4">
          <WaveDivider />
          <h2 className="text-3xl md:text-4xl font-black text-[#0B2545] tracking-tight">
            NOS EXCURSIONS
          </h2>
          <WaveDivider />
        </div>
        <p className="text-center text-slate-600 max-w-2xl mx-auto mb-12">
          Découvrez la Guadeloupe autrement grâce à nos excursions en mer au départ de la Marina du Gosier. Petits groupes, équipage local, souvenirs garantis.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {EXCURSIONS.map((ex) => (
            <button
              key={ex.id}
              onClick={() => setSelected(ex)}
              className="group text-left bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl border border-slate-100 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={ex.image}
                  alt={ex.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                {ex.badge && (
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[#FFC93C] text-[#0B2545] text-[10px] font-black tracking-wider px-2.5 py-1 rounded-full shadow-md">
                    <AlertTriangle className="h-3 w-3" />
                    {ex.badge}
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-black text-[#0B2545] text-xl tracking-tight text-center">
                  {ex.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 text-center leading-relaxed min-h-[40px]">
                  {ex.description}
                </p>
                <div className="mt-4 flex items-center justify-between pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-1.5 text-slate-500 text-xs font-medium">
                    <Clock className="h-4 w-4" />
                    <span>{ex.duration}</span>
                  </div>
                  <div className="text-[#25D366] font-black text-xl">
                    {ex.price}€
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <a
            href={buildWhatsAppLink("Bonjour, je souhaite avoir plus d'informations sur toutes vos excursions.")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#134074] text-white font-bold px-8 py-4 rounded-full shadow-lg transition-all duration-200 hover:-translate-y-0.5"
          >
            VOIR TOUTES NOS EXCURSIONS
          </a>
        </div>
      </div>

      {/* Details Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          {selected && (
            <>
              <div className="relative h-64">
                <img
                  src={selected.image}
                  alt={selected.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B2545] via-[#0B2545]/40 to-transparent" />
                <button
                  onClick={() => setSelected(null)}
                  className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5 text-[#0B2545]" />
                </button>
                <div className="absolute bottom-4 left-5 right-5">
                  <DialogHeader>
                    <DialogTitle className="text-white text-3xl font-black">
                      {selected.title}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="flex items-center gap-4 mt-2 text-white/90 text-sm">
                    <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {selected.duration}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> Marina du Gosier</span>
                    <span className="ml-auto bg-[#FFC93C] text-[#0B2545] font-black px-3 py-1 rounded-full">
                      {selected.price}€ / pers.
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {selected.notice && (
                  <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-amber-900 text-sm">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{selected.notice}</span>
                  </div>
                )}
                <p className="text-slate-700 leading-relaxed">{selected.description}</p>
                <h4 className="mt-5 font-bold text-[#0B2545] mb-3">Au programme :</h4>
                <ul className="space-y-2">
                  {selected.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700">
                      <Check className="h-5 w-5 text-[#25D366] shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={buildWhatsAppLink(`Bonjour, je souhaite réserver l'excursion ${selected.title}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold px-5 py-3 rounded-xl transition"
                >
                  <MessageCircle className="h-5 w-5" fill="white" strokeWidth={0} />
                  RÉSERVER CETTE EXCURSION
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Excursions;
